import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Location } from '@remix-run/router/history';
import { getHrefPathName } from '../../utils/url';
import { hasNextPageAnchorScroll, resetAnchorScrollPosition } from '../../components/PageAnchorScroll/utils';

export type RouterBrowserHistory = {
	push: (path: string, state?: unknown, options?: { scroll?: boolean }) => void;
	replace: (path: string, state?: unknown, options?: { scroll?: boolean }) => void;
	go: (n: number) => void;
	goBack: () => void;
	goForward: () => void;
	listen: (fn) => () => void;
	location: Location;
	state: Location['state'];
	length: number;
	action: string;
};

function useHistory(): RouterBrowserHistory {
	const navigate = useNavigate();
	const location = useLocation();

	// Maintain a collection of listeners
	const listeners = useRef([]);

	const listen = useCallback((fn) => {
		listeners.current.push(fn);
		// Return an unlisten function
		return () => {
			const index = listeners.current.indexOf(fn);
			if (index !== -1) {
				listeners.current.splice(index, 1);
			}
		};
	}, []);

	useEffect(() => {
		// Notify all listeners about the location change
		for (const fn of listeners.current) {
			fn(location);
		}
	}, [location]);

	return useMemo(
		() => ({
			push: (path: string, state?: unknown, options: { scroll?: boolean } = { scroll: true }) => {
				if (options?.scroll) {
					const nextPathName = getHrefPathName(path);

					if (hasNextPageAnchorScroll(nextPathName)) {
						resetAnchorScrollPosition(nextPathName);
					}
				}

				navigate(path, { state });
			},
			replace: (path: string, state?: unknown, options: { scroll?: boolean } = { scroll: true }) => {
				if (options?.scroll) {
					const nextPathName = getHrefPathName(path);

					if (hasNextPageAnchorScroll(nextPathName)) {
						resetAnchorScrollPosition(nextPathName);
					}
				}

				navigate(path, { replace: true, state });
			},
			go: (n: number) => {
				navigate(n);
			},
			goBack: () => {
				navigate(-1);
			},
			goForward: () => {
				navigate(1);
			},
			listen,
			location,
			state: location.state,
			length: typeof window !== 'undefined' && window.history ? window.history.length : 0,
			action: 'PUSH', // v6 doesn't have a direct equivalent. Defaulting to "PUSH".
		}),
		[listen, location, navigate],
	);
}

export { useHistory };
